import { Popover, Box, List, ListItem, ListItemButton } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import { useState, useRef } from 'react';
import { SearchInput } from '../../common/SearchInput';
import { categories, getMetadataCategory, ALL_CATEGORY } from './metadataCategories';
import { highlightMatch } from '../../../lib/highlightMatch';
import { getMetadataLabel } from './getMetadataLabel';
import { MetadataTypeProperties } from '@server/documentation';
import { useSelector } from 'react-redux';
import { selectMetadataTypes } from '../../../reducers/documentation/documentationPageReducer';

interface MetadataSelectionPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  onSelectMetadata: (metadataType: MetadataTypeProperties) => void;
  crmOrgId: string;
  selectedTypes?: string[];
}

export const MetadataSelectionPopover = ({
  isOpen,
  onClose,
  anchorEl,
  onSelectMetadata,
  crmOrgId,
  selectedTypes = [],
}: MetadataSelectionPopoverProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORY);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const metadataTypesData = useSelector(selectMetadataTypes(crmOrgId));
  const { types: metadataTypes, isLoading } = metadataTypesData ?? {
    types: [],
    isLoading: false,
  };

  // Filter out already selected types
  const availableTypes = metadataTypes.filter(
    (type: MetadataTypeProperties) => !selectedTypes.includes(type.name),
  );

  const filteredByCategory =
    selectedCategory === ALL_CATEGORY
      ? availableTypes
      : availableTypes.filter(
          (type: MetadataTypeProperties) => getMetadataCategory(type.name) === selectedCategory,
        );

  const filteredTypes = filteredByCategory.filter((type: MetadataTypeProperties) =>
    getMetadataLabel(type.name).toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSelectMetadata = (metadataType: MetadataTypeProperties) => {
    handleClosePopover();
    onSelectMetadata(metadataType);
  };

  const handleClosePopover = () => {
    setSearchTerm('');
    setSelectedCategory(ALL_CATEGORY);
    onClose();
  };

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategory(categoryId);
    // Scroll to top when category changes
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Popover
      open={isOpen}
      onClose={handleClosePopover}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
          width: '463px',
          mt: 1,
        },
      }}
    >
      <Box sx={{ p: 1 }} data-testid="add-metadata-popover">
        <Box sx={{ m: 2 }}>
          <SearchInput
            withFixedMagnifyingGlassIcon
            placeholder="Search"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            onClearButtonClick={() => setSearchTerm('')}
            autoFocus={true}
            TextFieldProps={{ value: searchTerm }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, height: '400px' }}>
          <Box
            sx={{
              width: '124px',
              borderRight: '1px solid',
              borderColor: colors.grey[120],
              pr: 0.5,
              pl: 0.5,
            }}
          >
            <List dense sx={{ p: 0 }}>
              {categories.map((category) => (
                <ListItem key={category.id} disablePadding sx={{ mb: 0 }}>
                  <ListItemButton
                    selected={selectedCategory === category.id}
                    onClick={() => handleCategoryClick(category.id)}
                    sx={{
                      height: '26px',
                      '&:hover': {
                        backgroundColor: colors.grey[100],
                      },
                      '&.Mui-selected': {
                        backgroundColor: colors.grey[100],
                        '&:hover': {
                          backgroundColor: colors.grey[100],
                        },
                      },
                      '& span': {
                        fontSize: '10px',
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      color={selectedCategory === category.id ? colors.blue[500] : 'text.secondary'}
                    >
                      {category.label}
                    </Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          <Box ref={scrollContainerRef} sx={{ flex: 1, overflow: 'auto' }}>
            {isLoading && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body" color="text.secondary">
                  Loading metadata types...
                </Typography>
              </Box>
            )}
            {!isLoading && (
              <>
                {selectedCategory === ALL_CATEGORY && searchTerm === '' && (
                  <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', ml: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      All metadata types
                    </Typography>
                  </Box>
                )}
                {filteredTypes.length > 0 ? (
                  <List dense sx={{ p: 0 }}>
                    {filteredTypes.map((metadataType: MetadataTypeProperties) => (
                      <ListItem key={metadataType.name} disablePadding sx={{ mb: 0 }}>
                        <ListItemButton
                          onClick={() => handleSelectMetadata(metadataType)}
                          sx={{
                            height: '32px',
                            '&:hover': {
                              backgroundColor: colors.grey[100],
                            },
                          }}
                        >
                          <Typography variant="caption" color="black">
                            {highlightMatch(getMetadataLabel(metadataType.name), searchTerm)}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 1 }}>
                    <Typography variant="caption" color={colors.grey[700]}>
                      No results found
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Popover>
  );
};
